import { UAParser } from "ua-parser-js";

interface ClientHints {
  browserVersion: string | null;
  model: string | null;
  platformVersion: string | null;
}

async function getClientHints(): Promise<ClientHints> {
  const empty: ClientHints = { browserVersion: null, model: null, platformVersion: null };

  if (!(navigator as any).userAgentData?.getHighEntropyValues) return empty;

  try {
    const hints = await (navigator as any).userAgentData.getHighEntropyValues([
      "fullVersionList",
      "model",
      "platformVersion",
    ]);

    const list: { brand: string; version: string }[] = hints.fullVersionList || hints.brands || [];
    const browserEntry = list.find((b) => !/not.?a.?brand/i.test(b.brand));

    return {
      browserVersion: browserEntry?.version ?? null,
      model: hints.model || null,
      // platformVersion on Android is "major.minor.patch" where major = Android version
      platformVersion: hints.platformVersion || null,
    };
  } catch {
    return empty;
  }
}

export async function getMetaInfo() {
  const parser = new UAParser();
  const { browser, os, device, ua } = parser.getResult();

  const hints = await getClientHints();

  // Client Hints platformVersion on Android: major digit = Android version (e.g. "13.0.0" → "13")
  const androidVersion =
    hints.platformVersion && os.name === "Android"
      ? hints.platformVersion.split(".")[0]
      : null;

  // Client Hints model is more accurate than the frozen UA model code on Android
  const resolvedModel = hints.model || device.model || null;
  const resolvedVendor = device.vendor || null;

  return {
    browser: {
      name: browser.name ?? null,
      version: hints.browserVersion ?? browser.version ?? null,
    },
    os: {
      name: os.name ?? null,
      version: androidVersion ?? os.version ?? null,
    },
    device: {
      type: device.type ?? "desktop",
      model: resolvedModel,
      name:
        [resolvedVendor, resolvedModel].filter(Boolean).join(" ") ||
        os.name ||
        null,
    },
    user_agent: ua,
    language: navigator.language ?? null,
    screen: {
      width: window.screen?.width ?? null,
      height: window.screen?.height ?? null,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
  };
}
